// Màn Đăng nhập (khung chung components/auth/AuthStage.tsx): tài khoản đã đăng nhập trên máy hiện thành danh sách đăng
// nhập nhanh (lib/rememberedAccounts.ts — không bao giờ lưu mật khẩu). Luồng giữ nguyên: login() → cần mã email thì
// /2fa, xong thì vào trang đúng vai trò. Nhập tài khoản mới: ô "Lưu tài khoản" (bật sẵn) — bỏ tick thì không nhớ lên máy.
// Bước "mã QR": điện thoại đã đăng nhập quét rồi duyệt (components/auth/QrLoginPanel.tsx, docs/KE_HOACH_DANG_NHAP_QR.md).

import { useEffect, useState, type KeyboardEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm, type UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { AlertCircle, ChevronLeft, ChevronRight, Eye, EyeOff, LockKeyhole, Phone, QrCode, UserPlus } from 'lucide-react'

import { Spinner } from '@/components/ui/spinner'
import { login, getAuthState, homeRouteOf } from '@/lib/auth'
import { ApiError } from '@/lib/api'
import { getHomeRouteForTenant } from '@/lib/permissions'
import { applyUpdateIfWaiting } from '@/lib/pwa'
import { AuthStage, AuthTitle } from '@/components/auth/AuthStage'
import { QrLoginPanel, type QrSignedIn } from '@/components/auth/QrLoginPanel'
import {
  KIND_LABEL, accountFromSession, forgetAccount, initialsOf, maskIdentifier, readRemembered, rememberAccount, setPendingLogin, takePendingLogin,
  type RememberedAccount,
} from '@/lib/rememberedAccounts'

const schema = z.object({
  identifier: z.string().min(1, 'Nhập số điện thoại hoặc email'),
  password: z.string().min(1, 'Nhập mật khẩu'),
})
type FormValues = z.infer<typeof schema>
/** accounts: danh sách tài khoản trên máy · password: nhập mật khẩu cho tài khoản đã chọn · form: tài khoản khác · qr: mã QR */
type Step = 'accounts' | 'password' | 'form' | 'qr'

// Chỉ tự focus khi có con trỏ chính xác (chuột). Trên máy cảm ứng, autofocus làm bàn phím ảo bật lên che mất nửa
// màn hình ngay khi vừa mở trang.
const finePointer = () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches

export default function LoginPage() {
  const nav = useNavigate()
  const [accounts, setAccounts] = useState<RememberedAccount[]>(readRemembered)
  const [step, setStep] = useState<Step>(() => (accounts.length ? 'accounts' : 'form'))
  const [picked, setPicked] = useState<RememberedAccount | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Nhớ tài khoản mới nhập lên máy sau khi đăng nhập được (tài khoản chọn từ danh sách thì đã nhớ sẵn) */
  const [save, setSave] = useState(true)
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { identifier: '', password: '' } })
  // Bản mới đang chờ (vd. vừa đăng xuất mà chưa bấm "Tải lại") → nạp trước khi đăng nhập, không chạy bản cũ
  useEffect(() => applyUpdateIfWaiting(), [])

  // Vừa chọn tài khoản (người dùng tự bấm) → đưa con trỏ vào ô mật khẩu luôn, kể cả trên điện thoại
  useEffect(() => {
    if (step === 'password') form.setFocus('password')
  }, [step, form])

  function go(next: Step, account: RememberedAccount | null = null) {
    setPicked(account)
    setError(null)
    form.reset({ identifier: account?.identifier ?? '', password: '' })
    setStep(next)
  }

  function forgetPicked() {
    if (!picked) return
    const next = forgetAccount(picked.id)
    setAccounts(next)
    toast.success('Đã bỏ lưu tài khoản')
    go(next.length ? 'accounts' : 'form')
  }

  async function onSubmit(v: FormValues) {
    setSubmitting(true)
    setError(null)
    try {
      const r = await login(v.identifier, v.password)
      const keep = step !== 'form' || save
      if (r.need2Fa) {
        // Hệ thống bật mã email: mã đã gửi về email của tài khoản — nhập đúng mã xong mới nhớ tài khoản (Verify2FA)
        if (keep) setPendingLogin(v.identifier)
        else takePendingLogin()
        nav('/2fa')
        return
      }
      const s = getAuthState()
      if (keep) rememberAccount(accountFromSession({ identifier: v.identifier, shops: s.shops, activeShopId: s.activeShopId, nppAdmin: s.nppAdmin }))
      toast.success('Đăng nhập thành công')
      nav(homeRouteOf(getAuthState(), getHomeRouteForTenant))
    } catch (err) {
      if (err instanceof ApiError && err.code === 'rate_limited') setError('Quá nhiều lần thử. Đợi vài phút rồi thử lại.')
      else setError((err as Error).message || 'Đăng nhập không thành công')
    } finally {
      setSubmitting(false)
    }
  }

  function onQrSignedIn(r: QrSignedIn) {
    const identifier = r.phone || r.email
    const s = getAuthState()
    if (save && identifier) rememberAccount(accountFromSession({ identifier, shops: s.shops, activeShopId: s.activeShopId, nppAdmin: s.nppAdmin }))
    toast.success('Đăng nhập thành công')
    nav(homeRouteOf(s, getHomeRouteForTenant))
  }

  const qrButton = (
    <button type="button" className="lg-btn2 lg-qr-entry" onClick={() => go('qr')}>
      <QrCode className="size-[18px]" aria-hidden />
      Đăng nhập bằng mã QR
    </button>
  )

  return (
    <AuthStage>
      <AuthTitle>Đăng nhập</AuthTitle>

      {step === 'accounts' && (
        <section aria-labelledby="saved-accounts">
          <p id="saved-accounts" className="lg-label">Tài khoản đã lưu</p>
          {accounts.map((a) => (
            <button key={a.id} type="button" className="lg-acct" onClick={() => go('password', a)}>
              <span className="lg-av" data-kind={a.kind} aria-hidden>{initialsOf(a.name)}</span>
              <span className="min-w-0 flex-1">
                <span className="lg-acct-name">{a.name}</span>
                <span className="lg-acct-sub">
                  <span className="lg-kind" data-kind={a.kind}>{KIND_LABEL[a.kind]}</span>
                  <span className="lg-acct-id">{maskIdentifier(a.identifier)}</span>
                </span>
              </span>
              <ChevronRight className="size-[18px] shrink-0 text-muted" aria-hidden />
            </button>
          ))}
          <div className="lg-or">hoặc</div>
          <button type="button" className="lg-btn2" onClick={() => go('form')}>
            <UserPlus className="size-[18px]" aria-hidden />
            Đăng nhập tài khoản khác
          </button>
          {qrButton}
        </section>
      )}

      {step === 'password' && picked && (
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="lg-who">
            <span className="lg-av lg-av-lg" data-kind={picked.kind} aria-hidden>{initialsOf(picked.name)}</span>
            <span className="min-w-0 flex-1">
              <span className="lg-who-name">{picked.name}</span>
              <span className="lg-acct-sub">
                <span className="lg-kind" data-kind={picked.kind}>{KIND_LABEL[picked.kind]}</span>
                <span className="lg-acct-id">{maskIdentifier(picked.identifier)}</span>
              </span>
            </span>
            <button type="button" className="lg-link" onClick={() => go('accounts')}>Đổi</button>
          </div>
          {/* Cho trình quản lý mật khẩu biết đang đăng nhập tài khoản nào */}
          <input type="text" autoComplete="username" value={picked.identifier} readOnly tabIndex={-1} aria-hidden className="sr-only" />
          <PasswordField form={form} />
          <FormEnd submitting={submitting} error={error} />
          <div className="mt-3 text-center">
            <button type="button" className="lg-link lg-link-muted" onClick={forgetPicked}>Bỏ lưu tài khoản này</button>
          </div>
        </form>
      )}

      {step === 'form' && (
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {accounts.length > 0 && (
            <button type="button" className="lg-back" onClick={() => go('accounts')}>
              <ChevronLeft className="size-[18px]" aria-hidden />
              Tài khoản đã lưu
            </button>
          )}
          <div className="lg-field" style={{ marginTop: accounts.length ? 6 : 0 }}>
            <div className="lg-field-head"><label htmlFor="identifier">Số điện thoại hoặc Email</label></div>
            <div className="lg-input-wrap">
              <Phone className="lg-input-icon" aria-hidden />
              <input
                id="identifier"
                type="text"
                autoComplete="username"
                autoFocus={finePointer()}
                placeholder="Nhập số điện thoại hoặc email"
                className="lg-input"
                aria-invalid={!!form.formState.errors.identifier}
                {...form.register('identifier')}
              />
            </div>
            {form.formState.errors.identifier && <p className="lg-hint">{form.formState.errors.identifier.message}</p>}
          </div>
          <PasswordField form={form} save={save} onSave={setSave} />
          <FormEnd submitting={submitting} error={error} />
          <div className="lg-or lg-qr-or">hoặc</div>
          {qrButton}
        </form>
      )}

      {step === 'qr' && (
        <section aria-label="Đăng nhập bằng mã QR">
          <button type="button" className="lg-back" onClick={() => go(accounts.length ? 'accounts' : 'form')}>
            <ChevronLeft className="size-[18px]" aria-hidden />
            Đăng nhập bằng mật khẩu
          </button>
          <QrLoginPanel onSignedIn={onQrSignedIn} />
          <div className="lg-qr-save">
            <label className="lg-remember">
              <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} />
              Lưu tài khoản
            </label>
          </div>
        </section>
      )}

      {(step === 'accounts' || step === 'form') && (
        <p className="lg-alt">
          Chưa có shop? <Link to="/signup">Đăng ký miễn phí</Link>
        </p>
      )}
      <p className="lg-fine">
        Bằng cách đăng nhập, bạn đồng ý với <Link to="/terms">Điều khoản sử dụng</Link> và{' '}
        <Link to="/privacy">Chính sách quyền riêng tư</Link>.
      </p>
    </AuthStage>
  )
}

/** Ô mật khẩu: nút ẩn/hiện vùng bấm 48×50, báo Caps Lock khi đang gõ. Có onSave → ô "Lưu tài khoản" cùng hàng "Quên mật khẩu?". */
function PasswordField({ form, save, onSave }: { form: UseFormReturn<FormValues>; save?: boolean; onSave?: (v: boolean) => void }) {
  const [show, setShow] = useState(false)
  const [caps, setCaps] = useState(false)
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => setCaps(e.getModifierState?.('CapsLock') ?? false)
  const reg = form.register('password')
  return (
    <div className="lg-field">
      <div className="lg-field-head"><label htmlFor="password">Mật khẩu</label></div>
      <div className="lg-input-wrap">
        <LockKeyhole className="lg-input-icon" aria-hidden />
        <input
          id="password"
          type={show ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="••••••••••"
          className="lg-input has-eye"
          aria-invalid={!!form.formState.errors.password}
          onKeyDown={onKey}
          onKeyUp={onKey}
          {...reg}
          onBlur={(e) => {
            setCaps(false)
            void reg.onBlur(e)
          }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="lg-eye"
          aria-label={show ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
          tabIndex={-1}
        >
          {show ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
        </button>
      </div>
      {form.formState.errors.password && <p className="lg-hint">{form.formState.errors.password.message}</p>}
      {caps && <p className="lg-caps">Đang bật Caps Lock</p>}
      {onSave ? (
        <div className="lg-pw-row">
          <label className="lg-remember">
            <input type="checkbox" checked={save} onChange={(e) => onSave(e.target.checked)} />
            Lưu tài khoản
          </label>
          <Link to="/forgot-password" className="lg-link">Quên mật khẩu?</Link>
        </div>
      ) : (
        <div className="mt-1 text-right">
          <Link to="/forgot-password" className="lg-link">Quên mật khẩu?</Link>
        </div>
      )}
    </div>
  )
}

function FormEnd({ submitting, error }: { submitting: boolean; error: string | null }) {
  return (
    <>
      {error && (
        <p className="lg-error" role="alert">
          <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      )}
      <button type="submit" className="lg-btn" disabled={submitting} data-busy={submitting || undefined}>
        {submitting ? <Spinner className="text-white" /> : null}
        {submitting ? 'Đang đăng nhập…' : 'Đăng nhập'}
      </button>
    </>
  )
}
